import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import { hasCode, isAuthExpired } from '@/api/errors'
import { livePolling } from '@/app/query-client'
import { brokerService } from '@/features/brokers/api/brokerService'
import { brokerKeys } from '@/features/brokers/api/keys'
import { useMarketStatus } from '@/hooks/useMarketStatus'
import type {
  BrokerByStock,
  BrokerDateRange,
  BrokerDetail,
  BrokerListQuery,
  BrokerListRow,
  BrokerOverview,
  BrokerPeer,
  BrokerPeriod,
  BrokerReview,
  BrokerReviews,
  BrokerReviewsQuery,
  BrokersBySector,
  BrokerStockFlow,
  BrokerStockSort,
  BrokerStocksQuery,
  BrokerTrade,
  BrokerTradesQuery,
  BusyName,
  BusyNamesQuery,
  Compare,
  LargeHoldings,
  LargeHoldingsQuery,
  LargeTradeLeader,
  LargeTradeLeadersQuery,
  LargeTrades,
  LargeTradesQuery,
  PVRange,
  PVRangeQuery,
  ReviewDraft,
  SectorBrokers,
  SectorFlow,
  StocksInCommon,
  StocksTradedMost,
  WindowedTablePage,
} from '@/schemas/broker'
import type { TablePage } from '@/schemas/table'

/**
 * The hooks the Broker Chirfaar screens call. One per response shape, keyed the
 * same way — see keys.ts for why the branches are separate.
 *
 * Every paged table takes `keepPreviousData` for the same reason floorsheet
 * does: a pager click must not blank the table or collapse the pager to "1"
 * while the next page loads.
 */

/**
 * `NO_FLOORSHEET_DATA` is not a failure on the six precomputed-only reads
 * below (stocks traded most, sector flow, sector brokers, brokers by sector,
 * large trades, large holdings) — it means "the nightly summary has not run
 * for this window yet", which is a routine, non-retryable wait, not an error
 * a route's `errorComponent` should show. docs/route-loading.md, "Not-found
 * answers": the `queryOptions` factory catches this ONE code and resolves
 * `null` instead of throwing, so a first-load loader (`ensureRouteData`)
 * settles normally and the screen keeps its own `data === null` branch —
 * the same shape Stock's `company === null` uses — instead of a route error
 * page. Any other error still throws.
 */
async function nullOnNoFloorsheetData<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read()
  } catch (error) {
    if (hasCode(error, 'NO_FLOORSHEET_DATA')) return null
    throw error
  }
}

/**
 * The same "Not-found answers" shape (docs/route-loading.md) as
 * `nullOnNoFloorsheetData`, for `STOCK_NOT_FOUND` on the two reads keyed by
 * a stock symbol (By stock, PV range): a typo'd or delisted symbol is not a
 * failure the route's `errorComponent` should own — the screen keeps its
 * own `data === null` branch, the same shape Stock's `company === null`
 * uses.
 */
async function nullOnStockNotFound<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read()
  } catch (error) {
    if (hasCode(error, 'STOCK_NOT_FOUND')) return null
    throw error
  }
}

/** The same shape again, for `BROKER_NOT_FOUND` on the two reads keyed by a broker code. */
async function nullOnBrokerNotFound<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read()
  } catch (error) {
    if (hasCode(error, 'BROKER_NOT_FOUND')) return null
    throw error
  }
}

// ── Overview ─────────────────────────────────────────────────────────────────

/**
 * Overview's main data is keyed on the `period` search param, so its route
 * loader awaits this factory directly (`ensureRouteData`) rather than a
 * suspense read — see docs/route-loading.md, "Search params". The hook
 * below is what `FloorStatsStrip` and `TopBrokersCard` already called;
 * spreading the factory keeps their key and fetch identical, only lifting
 * them out so the loader can share them.
 */
export const brokerOverviewQueryOptions = (period: BrokerPeriod) =>
  queryOptions<BrokerOverview>({
    queryKey: brokerKeys.overview(period),
    queryFn: () => brokerService.overview(period),
  })

export const useBrokerOverview = (period: BrokerPeriod): UseQueryResult<BrokerOverview> =>
  useQuery({ ...brokerOverviewQueryOptions(period), placeholderData: keepPreviousData })

/**
 * How many chips Overview's "Stocks traded most" card draws. The endpoint's
 * own default too, but sent explicitly so the card's count cannot move with a
 * backend default. Exported for the card's skeleton, which draws this many.
 */
export const STOCKS_TRADED_MOST_LIMIT = 10

/**
 * Overview's SECONDARY "Stocks traded most" read — its route loader prefetches
 * this, unawaited (`OverviewScreen.loader.ts`), and the card draws its own
 * skeleton and error. The limit is fixed here rather than passed in so the
 * loader and the card cannot key two different requests.
 */
export const stocksTradedMostQueryOptions = (period: BrokerPeriod) =>
  queryOptions<StocksTradedMost | null>({
    queryKey: brokerKeys.stocksTradedMost(period, STOCKS_TRADED_MOST_LIMIT),
    queryFn: () =>
      nullOnNoFloorsheetData(() =>
        brokerService.stocksTradedMost(period, STOCKS_TRADED_MOST_LIMIT),
      ),
  })

export const useStocksTradedMost = (
  period: BrokerPeriod,
): UseQueryResult<StocksTradedMost | null> =>
  useQuery({ ...stocksTradedMostQueryOptions(period), placeholderData: keepPreviousData })

// ── All brokers ──────────────────────────────────────────────────────────────

/**
 * Shared with `PVRangeScreen` and `BrokerScreen`'s own broker pickers — this
 * factory changes neither the key nor the fetch, only where they are
 * declared, so those callers are unaffected. All brokers' own route loader
 * awaits it directly for the entry URL's query (see AllBrokersScreen.loader.ts).
 */
export const brokerListQueryOptions = (query: BrokerListQuery) =>
  queryOptions<TablePage<BrokerListRow>>({
    queryKey: brokerKeys.list(query),
    queryFn: () => brokerService.list(query),
  })

export const useBrokerList = (query: BrokerListQuery): UseQueryResult<TablePage<BrokerListRow>> =>
  useQuery({ ...brokerListQueryOptions(query), placeholderData: keepPreviousData })

// ── Most traded ──────────────────────────────────────────────────────────────

/**
 * `WindowedTablePage`, not `TablePage`: busy-names is the one paged broker
 * read whose `meta` reports the window the page was taken over, so the screen
 * can say which session it is showing. `window` is null when the backend sent
 * none — see `windowedTablePageSchema` in `@/schemas/broker`.
 */
/**
 * Most traded's main data — its route loader awaits this for the entry URL's
 * query. Overview's "Sectors traded most" card reads it too, one sector per
 * row, for that row's ticker chips.
 */
export const busyNamesQueryOptions = (query: BusyNamesQuery) =>
  queryOptions<WindowedTablePage<BusyName>>({
    queryKey: brokerKeys.busyNames(query),
    queryFn: () => brokerService.busyNames(query),
  })

/**
 * `enabled` is for the stock picker, which reads Most traded's first page as
 * its opening list and has no reason to ask for it until the panel opens.
 */
export const useBusyNames = (
  query: BusyNamesQuery,
  enabled = true,
): UseQueryResult<WindowedTablePage<BusyName>> =>
  useQuery({ ...busyNamesQueryOptions(query), placeholderData: keepPreviousData, enabled })

// ── Sector flow ──────────────────────────────────────────────────────────────

/**
 * Precomputed-only. An empty `sectors` here means the nightly summary has not
 * run, NOT that no money moved — the screen's empty state has to say so, which
 * is why the hook does not translate it into anything friendlier.
 */
/**
 * Sector flow's own MAIN data (the overview branch), and a SECONDARY read on
 * two other pages: Most traded (only to populate the sector filter's menu —
 * see `MostTradedScreen.loader.ts`) and Overview (its "Sectors traded most"
 * card — see `OverviewScreen.loader.ts`). Every loader awaits or prefetches
 * this factory directly, so all three pages share one cache entry per period.
 */
export const sectorFlowQueryOptions = (period: BrokerPeriod) =>
  queryOptions<SectorFlow | null>({
    queryKey: brokerKeys.sectorFlow(period),
    queryFn: () => nullOnNoFloorsheetData(() => brokerService.sectorFlow(period)),
  })

export const useSectorFlow = (period: BrokerPeriod): UseQueryResult<SectorFlow | null> =>
  useQuery({ ...sectorFlowQueryOptions(period), placeholderData: keepPreviousData })

/** Sector flow's MAIN data for the drilldown branch (`?sector=` present). */
export const sectorBrokersQueryOptions = (sector: string, period: BrokerPeriod) =>
  queryOptions<SectorBrokers | null>({
    queryKey: brokerKeys.sectorBrokers(sector, period),
    queryFn: () => nullOnNoFloorsheetData(() => brokerService.sectorBrokers(sector, period)),
  })

export const useSectorBrokers = (
  sector: string,
  period: BrokerPeriod,
): UseQueryResult<SectorBrokers | null> =>
  useQuery({ ...sectorBrokersQueryOptions(sector, period), placeholderData: keepPreviousData })

/**
 * How many broker rows Sector flow's "Broker by sector" card draws. Sent
 * explicitly (the endpoint defaults to 50) so the card's row count cannot move
 * with a backend default. Exported for the card's skeleton, which draws this
 * many.
 */
export const BROKERS_BY_SECTOR_LIMIT = 12

/**
 * Sector flow's SECONDARY "Broker by sector" read — the route loader prefetches
 * this, unawaited, on the overview branch only (`SectorFlowScreen.loader.ts`),
 * and the card draws its own skeleton and error. The limit is fixed here
 * rather than passed in so the loader and the card cannot key two different
 * requests.
 */
export const brokersBySectorQueryOptions = (period: BrokerPeriod) =>
  queryOptions<BrokersBySector | null>({
    queryKey: brokerKeys.brokersBySector(period, BROKERS_BY_SECTOR_LIMIT),
    queryFn: () =>
      nullOnNoFloorsheetData(() => brokerService.brokersBySector(period, BROKERS_BY_SECTOR_LIMIT)),
  })

export const useBrokersBySector = (period: BrokerPeriod): UseQueryResult<BrokersBySector | null> =>
  useQuery({ ...brokersBySectorQueryOptions(period), placeholderData: keepPreviousData })

// ── Large trades ─────────────────────────────────────────────────────────────

/**
 * Tiles and the trade list together, because the backend serves them together.
 *
 * Polls while NEPSE is open — this is a log that grows at the head with every
 * execution, unlike the aggregates that settle over a session. Gated on market
 * status like every other live query, so an overnight tab issues nothing.
 */
/**
 * Large trades' MAIN data. Factory = key + fetch only, no polling: that stays
 * on the hook (`livePolling`), which the route loader never wants — a loader
 * that awaited a polling subscription would never resolve.
 */
export const largeTradesQueryOptions = (query: LargeTradesQuery) =>
  queryOptions<LargeTrades | null>({
    queryKey: brokerKeys.largeTrades(query),
    queryFn: () => nullOnNoFloorsheetData(() => brokerService.largeTrades(query)),
  })

export const useLargeTrades = (query: LargeTradesQuery): UseQueryResult<LargeTrades | null> => {
  const { isOpen } = useMarketStatus()

  return useQuery({
    ...largeTradesQueryOptions(query),
    placeholderData: keepPreviousData,
    refetchInterval: livePolling(isOpen),
  })
}

/** Large trades' SECONDARY read — `BrokerLeadersCard` draws its own skeleton and error for it. */
export const largeTradeLeadersQueryOptions = (query: LargeTradeLeadersQuery) =>
  queryOptions<LargeTradeLeader[]>({
    queryKey: brokerKeys.largeTradeLeaders(query),
    queryFn: () => brokerService.largeTradeLeaders(query),
  })

export const useLargeTradeLeaders = (
  query: LargeTradeLeadersQuery,
): UseQueryResult<LargeTradeLeader[]> =>
  useQuery({ ...largeTradeLeadersQueryOptions(query), placeholderData: keepPreviousData })

// ── Large holdings ───────────────────────────────────────────────────────────

/**
 * The biggest net broker positions, as a share of each company's paid-up
 * capital.
 *
 * `LargeHoldings`, not a `TablePage`: the paging is inside the payload (the
 * endpoint returns a plain object, not a list envelope), so the pager reads
 * `data.page` / `data.total` the same way Large trades' does.
 *
 * No `refetchInterval`. Unlike large trades — a log that grows at the head
 * with every execution — this reads the precomputed `broker_symbol_stats`
 * table, which only moves when the nightly summary runs, so polling it during
 * a session would issue requests that can only ever return the same rows.
 */
/** Large holdings' MAIN data — its route loader awaits this for the entry URL's query. */
export const largeHoldingsQueryOptions = (query: LargeHoldingsQuery) =>
  queryOptions<LargeHoldings | null>({
    queryKey: brokerKeys.largeHoldings(query),
    queryFn: () => nullOnNoFloorsheetData(() => brokerService.largeHoldings(query)),
  })

export const useLargeHoldings = (query: LargeHoldingsQuery): UseQueryResult<LargeHoldings | null> =>
  useQuery({ ...largeHoldingsQueryOptions(query), placeholderData: keepPreviousData })

// ── PV range ─────────────────────────────────────────────────────────────────

/**
 * One stock's volume profile by price bucket.
 *
 * Polls while NEPSE is open, like `useLargeTrades` and `useBrokerFloorsheet`:
 * this reads `chirfaar_floor_sheet` directly with no precompute in the way, so
 * a bucket genuinely grows as the session runs. Gated on market status, so an
 * overnight tab issues nothing.
 *
 * `enabled` on the symbol, matching `useBrokersByStock`: an empty path segment
 * is not a stock, and requesting one would be a guaranteed 404.
 */
/**
 * PV range answers TWO different non-throwing misses, not one, so this stays
 * a discriminated union rather than collapsing both to `null` the way
 * `nullOnStockNotFound`/`nullOnNoFloorsheetData` do elsewhere: a bad symbol
 * (`STOCK_NOT_FOUND`) gets a "browse stock prices" way out, and a floorsheet
 * that has not settled yet (`NO_FLOORSHEET_DATA`) does not — the two copy the
 * screen already drew before this loader existed, and "Not-found answers"
 * (docs/route-loading.md) still applies (retrying either is pointless, so
 * neither is a route error).
 * `PVRange` is always an object; a miss is always a plain string, so the two
 * are cheap to tell apart with `typeof`.
 */
export type PVRangeMiss = 'not-found' | 'no-floorsheet'

/**
 * PV range's MAIN data — its route loader awaits this for the entry URL's
 * query (`PVRangeScreen.loader.ts`). Factory = key + fetch only: `enabled`
 * and the poll stay on the hook, matching `largeTradesQueryOptions` above.
 */
export const pvRangeQueryOptions = (symbol: string, query: PVRangeQuery) =>
  queryOptions<PVRange | PVRangeMiss>({
    queryKey: brokerKeys.pvRange(symbol, query),
    queryFn: async () => {
      try {
        return await brokerService.pvRange(symbol, query)
      } catch (error) {
        if (hasCode(error, 'STOCK_NOT_FOUND')) return 'not-found'
        if (hasCode(error, 'NO_FLOORSHEET_DATA')) return 'no-floorsheet'
        throw error
      }
    },
  })

export const usePVRange = (
  symbol: string,
  query: PVRangeQuery,
): UseQueryResult<PVRange | PVRangeMiss> => {
  const { isOpen } = useMarketStatus()

  return useQuery({
    ...pvRangeQueryOptions(symbol, query),
    enabled: symbol.length > 0,
    placeholderData: keepPreviousData,
    refetchInterval: livePolling(isOpen),
  })
}

// ── Compare ──────────────────────────────────────────────────────────────────

/**
 * `brokers` is ordered and that order is the column order, so it is part of the
 * key. `enabled` keeps the hook silent until the member has picked the two the
 * endpoint requires — an empty or one-broker selection is a 400, not a query.
 */
/**
 * Compare's "THE NUMBERS" card. `enabled` stays on the hook, never the
 * factory: a loader only calls this when the entry URL already carries 2-4
 * codes (`CompareScreen.loader.ts`), so it has no need for the guard the
 * hook keeps for a member still picking.
 */
export const brokerCompareQueryOptions = (brokers: readonly string[], period: BrokerPeriod) =>
  queryOptions<Compare>({
    queryKey: brokerKeys.compare(brokers, period),
    queryFn: () => brokerService.compare(brokers, period),
  })

export const useBrokerCompare = (
  brokers: readonly string[],
  period: BrokerPeriod,
): UseQueryResult<Compare> =>
  useQuery({ ...brokerCompareQueryOptions(brokers, period), enabled: brokers.length >= 2 })

export const stocksInCommonQueryOptions = (brokers: readonly string[], period: BrokerPeriod) =>
  queryOptions<StocksInCommon>({
    queryKey: brokerKeys.stocksInCommon(brokers, period),
    queryFn: () => brokerService.stocksInCommon(brokers, period),
  })

export const useStocksInCommon = (
  brokers: readonly string[],
  period: BrokerPeriod,
): UseQueryResult<StocksInCommon> =>
  useQuery({ ...stocksInCommonQueryOptions(brokers, period), enabled: brokers.length >= 2 })

// ── By stock ─────────────────────────────────────────────────────────────────

/**
 * Shared with StockScreen, which starts the Broker holding tab's read from
 * the URL rather than after the company detail lands.
 *
 * `STOCK_NOT_FOUND` resolves `null` rather than throwing ("Not-found
 * answers", docs/route-loading.md) — `ByStockScreen`'s route loader awaits
 * this directly, and a bad symbol must land the member on the screen's own
 * not-found copy, not the route's generic error page with a "Try again"
 * that would repeat the same 404 forever. `BrokerHoldingPanel`
 * (StockScreen's tab) reads the same key through this same factory and has
 * its own `data === null` branch already.
 */
export const brokersByStockQueryOptions = (
  symbol: string,
  period: BrokerPeriod,
  range: BrokerDateRange | null = null,
) =>
  queryOptions<BrokerByStock | null>({
    queryKey: brokerKeys.byStock(symbol, period, range),
    queryFn: () => nullOnStockNotFound(() => brokerService.byStock(symbol, period, range)),
  })

export const useBrokersByStock = (
  symbol: string,
  period: BrokerPeriod,
  range: BrokerDateRange | null = null,
): UseQueryResult<BrokerByStock | null> =>
  useQuery({
    ...brokersByStockQueryOptions(symbol, period, range),
    enabled: symbol.length > 0,
    placeholderData: keepPreviousData,
  })

// ── By broker ────────────────────────────────────────────────────────────────

/**
 * The whole By broker page in one query.
 *
 * Overview, Activity and Specialty read different blocks of this one result;
 * they are not three hooks, because they were never three requests.
 */
/**
 * By broker's MAIN data — its route loader awaits this for the entry URL's
 * period (`BrokerScreen.loader.ts`), and Reviews' own loader prefetches it
 * too, unawaited, for the header's broker name (`BrokerReviewsScreen.loader.ts`).
 *
 * `BROKER_NOT_FOUND` resolves `null` rather than throwing
 * (docs/route-loading.md, "Not-found answers") — a broker number that does
 * not exist is not retryable, so `BrokerScreen` keeps its own
 * `data === null` branch instead of the route's generic error page
 * repeating the same 404 on "Try again".
 */
export const brokerDetailQueryOptions = (code: string, period: BrokerPeriod) =>
  queryOptions<BrokerDetail | null>({
    queryKey: brokerKeys.detail(code, period),
    queryFn: () => nullOnBrokerNotFound(() => brokerService.detail(code, period)),
  })

export const useBrokerDetail = (
  code: string,
  period: BrokerPeriod,
): UseQueryResult<BrokerDetail | null> =>
  useQuery({
    ...brokerDetailQueryOptions(code, period),
    enabled: code.length > 0,
    placeholderData: keepPreviousData,
  })

/**
 * By broker's SECONDARY "every stock traded" table — its route loader
 * prefetches this for the entry URL's search/sort/page, unawaited, so the
 * card's own `CardState` skeleton is already resolving before the page even
 * mounts (`BrokerScreen.loader.ts`).
 */
export const brokerStocksQueryOptions = (code: string, query: BrokerStocksQuery) =>
  queryOptions<TablePage<BrokerStockFlow>>({
    queryKey: brokerKeys.stocks(code, query),
    queryFn: () => brokerService.stocks(code, query),
  })

export const useBrokerStocks = (
  code: string,
  query: BrokerStocksQuery,
): UseQueryResult<TablePage<BrokerStockFlow>> =>
  useQuery({
    ...brokerStocksQueryOptions(code, query),
    enabled: code.length > 0,
    placeholderData: keepPreviousData,
  })

/**
 * Every stock the broker traded in the window, walked to exhaustion.
 *
 * Compare's "Stocks in common" diff needs the SET, not a page of it — see
 * `brokerService.allStocks` for what a page cost that tab's two legend counts.
 * `keepPreviousData` is deliberately absent: this feeds a client-side diff
 * across up to four brokers, and holding one broker's previous list beside three
 * fresh ones would draw a matrix mixing two windows.
 */
export const useAllBrokerStocks = (
  code: string,
  query: { period: BrokerPeriod; sort: BrokerStockSort },
): UseQueryResult<BrokerStockFlow[]> =>
  useQuery<BrokerStockFlow[]>({
    queryKey: brokerKeys.allStocks(code, query),
    queryFn: () => brokerService.allStocks(code, query),
    enabled: code.length > 0,
  })

/** The broker's own contracts. Polls while the market is open — see useLargeTrades. */
export const useBrokerFloorsheet = (
  code: string,
  query: BrokerTradesQuery,
): UseQueryResult<TablePage<BrokerTrade>> => {
  const { isOpen } = useMarketStatus()

  return useQuery<TablePage<BrokerTrade>>({
    queryKey: brokerKeys.floorsheet(code, query),
    queryFn: () => brokerService.floorsheet(code, query),
    enabled: code.length > 0,
    placeholderData: keepPreviousData,
    refetchInterval: livePolling(isOpen),
  })
}

/**
 * By broker's SECONDARY "ranked nearby" strip — its route loader prefetches
 * this too, unawaited (`BrokerScreen.loader.ts`).
 */
export const brokerPeersQueryOptions = (code: string) =>
  queryOptions<BrokerPeer[]>({
    queryKey: brokerKeys.peers(code),
    queryFn: () => brokerService.peers(code),
  })

export const useBrokerPeers = (code: string): UseQueryResult<BrokerPeer[]> =>
  useQuery({ ...brokerPeersQueryOptions(code), enabled: code.length > 0 })

// ── Reviews ──────────────────────────────────────────────────────────────────

/**
 * Reviews' MAIN data — its route loader awaits this for the entry URL's page
 * (`BrokerReviewsScreen.loader.ts`). Public: the average, the histogram and
 * one page of reviews.
 *
 * `BROKER_NOT_FOUND` resolves `null` rather than throwing, the same
 * `nullOnBrokerNotFound` shape `brokerDetailQueryOptions` uses — see its
 * own comment.
 */
export const brokerReviewsQueryOptions = (code: string, query: BrokerReviewsQuery) =>
  queryOptions<BrokerReviews | null>({
    queryKey: brokerKeys.reviews(code, query),
    queryFn: () => nullOnBrokerNotFound(() => brokerService.reviews(code, query)),
  })

export const useBrokerReviews = (
  code: string,
  query: BrokerReviewsQuery,
): UseQueryResult<BrokerReviews | null> =>
  useQuery({
    ...brokerReviewsQueryOptions(code, query),
    enabled: code.length > 0,
    placeholderData: keepPreviousData,
  })

/**
 * Why a review could not be posted, in the four terms the screen can act on.
 *
 * Branching on `error.code`, never on `error.message` — the code is the
 * contract and the message is not. `signInRequired` is the one that must stay
 * distinct: it is not a failure of the review, it is a missing session, and the
 * screen opens the sign-in sheet and re-submits rather than showing red text
 * under the textarea.
 */
export type ReviewSubmitFailure = 'signInRequired' | 'invalid' | 'brokerNotFound' | 'failed'

export const reviewSubmitFailure = (error: unknown): ReviewSubmitFailure | null => {
  if (error === null || error === undefined) return null
  // isAuthExpired is the same predicate the HTTP client uses to decide a
  // session is over, so "prompt sign-in" here and "sign out" there can never
  // disagree about what AUTHENTICATION_REQUIRED means.
  if (isAuthExpired(error)) return 'signInRequired'
  if (hasCode(error, 'VALIDATION_ERROR')) return 'invalid'
  if (hasCode(error, 'BROKER_NOT_FOUND')) return 'brokerNotFound'
  return 'failed'
}

/**
 * The one write path in Broker Chirfaar.
 *
 * An upsert: one review per user per broker, so submitting twice edits rather
 * than duplicates, and the hook is `useSubmitBrokerReview` rather than
 * `useCreate…` to stop a screen from drawing a "Post" button that the backend
 * will treat as "Edit".
 *
 * Invalidates two branches, not one. The reviews pages obviously move; so does
 * the rating in the By broker header, which is the same average shown a second
 * time. Nothing else about the broker changes, so its stocks, contracts and
 * peers are left alone rather than being swept up by invalidating the whole
 * `broker(code)` scope.
 */
export const useSubmitBrokerReview = (
  code: string,
): UseMutationResult<BrokerReview, unknown, ReviewDraft> => {
  const queryClient = useQueryClient()

  return useMutation<BrokerReview, unknown, ReviewDraft>({
    mutationFn: (draft: ReviewDraft) => brokerService.createReview(code, draft),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: brokerKeys.reviewsFor(code) }),
        queryClient.invalidateQueries({ queryKey: brokerKeys.detailFor(code) }),
      ])
    },
  })
}
